import {Button, Grid} from '@material-ui/core';
import {H6} from 'components/TypographyShortcuts';
import {useContext, useEffect, useState} from 'react';
import {PSingleLine} from 'components/TypographyShortcuts';
import usePostVesselSearch from 'hooks/usePostVesselSearch';
import VesselModal from './VesselModal';
import {VesselCard, NoVessel} from './VesselCard';
import VesselSearch from './VesselSearch';
import MyVessels from './MyVessels';
import {NewVessel} from './VesselCard';
import {Section} from '..';
import {bootstrapOldForm} from '../AutoSection';
import {widgetFields} from './fields';
import {AutoField} from '../fields';
import {useFieldData} from '../hooks';
import {useGetUserVessels, useGetVessel} from 'hooks/useGetEndpoint';
import {selectedShadow} from './VesselCard';
import {VesselLoading} from './VesselCard';
import {getCurrentState} from '../utils/state';
import {FormState} from 'components/forms/context';
import {useClearVessel} from 'components/forms/hooks/useClearVessel';
import Show from 'components/utils/Show';
import {sanitizeFormInput} from './VesselEndSection';

const VesselWidget = () => {
    // network
    const [query, setQuery] = useState('');
    const [pageNumber, setPageNumber] = useState(0);
    const searchResults = usePostVesselSearch(query, pageNumber);
    const hasMore = searchResults?.hasMore;
    const {data: recommended} = useGetUserVessels();
    const handleQueryChange = e => {
        setQuery(e.target.value);
        setPageNumber(1);
    };

    // UI interactivity
    const [selectedId, setSelectedId] = useFieldData('vessel_id');
    const [selected, setSelected] = useState(null);
    const [newVessel, setNewVessel] = useFieldData('new_vessel');
    const [detailVessel, setDetailVessel] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const {node, navigate} = useContext(FormState);

    const {data: existingSelected, isLoading} = useGetVessel(selectedId);
    const {clearAndUpdate} = useClearVessel();

    useEffect(() => {
        if (existingSelected) {
            setSelected(existingSelected);
        }
        //eslint-disable-next-line
    }, [existingSelected]);

    const clearAndReturnLatest = data => {
        clearAndUpdate(data);
        return getCurrentState(`forms[${node}]`);
    };

    const handleSelectVessel = vessel => {
        // adds a vessel into form state
        // remove vessel fields from form state if there are any leftover from editing
        // at the end should only be field vessel_id pointing to correct vessel
        setSelected(vessel);
        setNewVessel(null);
        const newState = clearAndReturnLatest({
            vessel_id: vessel?.vessel_id,
            vessel_type: vessel?.vessel_type,
            vessel_name: vessel?.vessel_name,
            new_vessel: null,
        });
        navigate(1, newState);
    };

    const handleDeselectVessel = () => {
        setSelected(null);
        setSelectedId(null);
        setNewVessel(null);
        setDetailVessel(null);
    };

    const handleEditVessel = vessel => {
        const newState = clearAndReturnLatest({
            vessel_id: null,
            ...vessel,
            new_vessel: 'edit',
        });
        navigate(1, newState);
    };

    const handleNewVessel = () => {
        const newState = clearAndReturnLatest({
            vessel_id: null,
            new_vessel: 'new',
        });
        navigate(1, newState);
    };

    const handleDetailVesselUpdate = vessel => {
        // selects a vessel to show in the VesselModal
        // we sanitize this becuase for it feeds into the modal form
        const cleanVessel = sanitizeFormInput(vessel);
        setDetailVessel(cleanVessel);
        setDetailModalOpen(true);
    };

    const {schema, intiialValues} = bootstrapOldForm('Select', widgetFields);

    return [
        <Section
            {...{schema, intiialValues}}
            gridify={false}
            key='form-section'
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <H6>Vessel Search</H6>
                </Grid>
                <Grid item xs={12}>
                    <PSingleLine align='center'>
                        Which vessel is listed on this form?
                    </PSingleLine>
                </Grid>
                <Grid item xs={12} container justifyContent='center'>
                    {newVessel === 'new' ? (
                        <NewVessel />
                    ) : selected ? (
                        <VesselCard
                            vessel={selected}
                            selectedId={selectedId}
                            handleDetailVesselUpdate={handleDetailVesselUpdate}
                            context='selected-vessel'
                        />
                    ) : isLoading ? (
                        <VesselLoading />
                    ) : (
                        <NoVessel />
                    )}
                </Grid>
                <Grid item xs={12}>
                    <MyVessels
                        recommended={recommended}
                        selectedId={selectedId}
                        handleDetailVesselUpdate={handleDetailVesselUpdate}
                    />
                </Grid>
                <Grid item xs={12}>
                    <VesselSearch
                        hasRecommended={recommended?.length}
                        query={query}
                        handleQueryChange={handleQueryChange}
                        searchResults={searchResults}
                        selectedId={selectedId}
                        handleDetailVesselUpdate={handleDetailVesselUpdate}
                        setPageNumber={setPageNumber}
                        hasMore={hasMore}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button
                        variant='contained'
                        color='primary'
                        fullWidth
                        onClick={handleNewVessel}
                        style={{boxShadow: newVessel ? selectedShadow : null}}
                        data-cy='new-vessel'
                    >
                        I don't see my Boat
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    <div style={{margin: '-2em 0em'}}>
                        <AutoField
                            field={widgetFields.vessel_id}
                            name='vessel_id'
                            value={selected?.id || ''}
                            style={{display: 'inline-flex'}}
                        />
                        <AutoField
                            field={widgetFields.new_vessel}
                            name='new_vessel'
                            value={newVessel || ''}
                        />
                    </div>
                </Grid>
            </Grid>
        </Section>,
        <Show condition={detailVessel} key='modal'>
            <VesselModal
                vessel={detailVessel}
                updateVessel={setDetailVessel}
                selected={selected}
                handleSelectVessel={handleSelectVessel}
                handleDeselectVessel={handleDeselectVessel}
                handleEditVessel={handleEditVessel}
                open={detailModalOpen}
                setOpen={setDetailModalOpen}
            />
        </Show>,
    ];
};

export default VesselWidget;
